import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { navCategories } from '@/mocks/navigationData';

export default function HeroSection() {
  const [searchQuery, setSearchQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const navigate = useNavigate();

  const allSites = navCategories.flatMap((cat) =>
    cat.sites.map((site) => ({ ...site, categoryName: cat.name, categorySlug: cat.slug }))
  );

  const suggestions = searchQuery.trim()
    ? allSites.filter(
        (s) =>
          s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          s.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
          s.tags.some((t) => t.toLowerCase().includes(searchQuery.toLowerCase()))
      ).slice(0, 8)
    : [];

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      const matched = allSites.find(
        (s) => s.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
      if (matched) {
        navigate(`/category/${matched.categorySlug}`);
      }
    }
  };

  const popularSearches = ['百度', 'GitHub', '知乎', '抖音', '淘宝', 'Notion'];

  return (
    <section className="relative w-full min-h-[500px] md:min-h-[600px] flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Abstract%20geometric%20landscape%20with%20warm%20golden%20and%20cream%20tones%2C%20soft%20flowing%20shapes%20inspired%20by%20navigation%20and%20compass%2C%20minimalist%20modern%20design%2C%20subtle%20gradients%2C%20clean%20atmospheric%20composition%20with%20organic%20curves%2C%20light%20airy%20aesthetic%2C%20no%20text%20or%20text%20elements%2C%20warm%20natural%20color%20palette&width=1600&height=900&seq=hero-bg-2025&orientation=landscape"
          alt="蓝导航背景"
          className="w-full h-full object-cover object-top"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-black/15 via-black/10 to-black/20"></div>

      <div className="relative z-10 w-full max-w-[900px] mx-auto px-4 md:px-6 py-16 md:py-24 text-center">
        <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight">
          精选网址导航
          <span className="block text-2xl md:text-3xl lg:text-4xl font-medium mt-2 text-white/90">
            一站直达精彩
          </span>
        </h1>
        <p className="text-white/80 text-base md:text-lg max-w-xl mx-auto mb-8 leading-relaxed">
          汇聚海量优质网站资源，分类清晰、访问便捷，帮您快速定位所需网站
        </p>

        <form onSubmit={handleSearch} className="relative max-w-xl mx-auto mb-8">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <i className="ri-search-line text-lg text-foreground-400"></i>
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setShowSuggestions(true);
              }}
              onFocus={() => setShowSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              placeholder="搜索网站名称、描述或标签..."
              className="w-full pl-11 pr-4 py-3.5 rounded-2xl bg-white/95 backdrop-blur-sm text-foreground-900 text-sm placeholder:text-foreground-400 border-0 focus:outline-none focus:ring-2 focus:ring-primary-400 transition-shadow"
              autoComplete="off"
            />
            <button
              type="submit"
              className="absolute right-2 top-1/2 -translate-y-1/2 px-5 py-2 rounded-xl bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
            >
              搜索
            </button>
          </div>

          {showSuggestions && suggestions.length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl border border-background-200 overflow-hidden shadow-lg animate-[fadeIn_0.15s_ease-out] z-20">
              {suggestions.map((site) => (
                <button
                  key={site.id}
                  type="button"
                  onMouseDown={() => {
                    navigate(`/category/${site.categorySlug}`);
                    setSearchQuery('');
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-background-100 transition-colors text-left"
                >
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100">
                    <i className={`${site.icon} text-base text-foreground-600`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground-900">{site.name}</p>
                    <p className="text-xs text-foreground-500 truncate">{site.description}</p>
                  </div>
                  <span className="text-xs text-foreground-400 bg-background-100 px-2 py-0.5 rounded-full whitespace-nowrap">
                    {site.categoryName}
                  </span>
                </button>
              ))}
            </div>
          )}
        </form>

        <div className="flex items-center justify-center gap-2 flex-wrap">
          <span className="text-white/60 text-xs">热门搜索：</span>
          {popularSearches.map((term) => (
            <button
              key={term}
              onClick={() => {
                setSearchQuery(term);
                const matched = allSites.find((s) => s.name.includes(term));
                if (matched) navigate(`/category/${matched.categorySlug}`);
              }}
              className="px-3 py-1 rounded-full border border-white/30 text-white/80 text-xs hover:bg-white/15 hover:border-white/50 transition-all duration-200 whitespace-nowrap"
            >
              {term}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}